import "./conditionalProgramTabs.css";
import { Button } from "../ui/button";
import { Heart, UserRound, Link, CheckCircle, Check } from "lucide-react";

export const ProgramFriendRequestsTab = ({
  friendRequestsData,
  friendRequestsLoading,
  friendRequestsError,
  onRetry,
  onMarkProcessed,
}) => {
  if (friendRequestsLoading) {
    return (
      <div
        role="status" aria-live="polite"
        className="program-section program-conditional flex items-center justify-center gap-3 py-8"
        data-testid="friend-requests-tab"
      >
        <div aria-hidden="true" className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
        Loading friend requests…
      </div>
    );
  }

  if (friendRequestsError) {
    return (
      <div role="alert" className="program-conditional program-data-panel" data-testid="friend-requests-tab">
        <p>{friendRequestsError}</p>
        <Button variant="outline" onClick={onRetry}>Try again</Button>
      </div>
    );
  }

  if (!friendRequestsData) {
    return (
      <div
        className="program-section program-conditional program-empty-state"
        data-testid="friend-requests-tab"
      >
        <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          No Friend Requests
        </h3>
        <p className="text-slate-500">
          Friend requests will appear here once parents submit them during
          registration.
        </p>
      </div>
    );
  }

  return (
    <div className="program-section program-conditional program-friend-requests" data-testid="friend-requests-tab">
      <div className="program-section-heading"><div><h2>Friend requests</h2><p>Review mutual matches and mark requests as processed after team placement.</p></div></div>
      {/* Summary Stats */}
      <div className="program-section-metrics">
        <div className="bg-white border border-slate-200/60 rounded-2xl p-4 text-center">
          <p className="text-3xl font-semibold text-blue-600">
            {friendRequestsData.summary.total_requests}
          </p>
          <p className="text-sm text-slate-500">Total Requests</p>
        </div>
        <div className="bg-white border border-slate-200/60 rounded-2xl p-4 text-center">
          <p className="text-3xl font-semibold text-green-600">
            {friendRequestsData.summary.matched}
          </p>
          <p className="text-sm text-slate-500">Mutual Matches</p>
        </div>
        <div className="bg-white border border-slate-200/60 rounded-2xl p-4 text-center">
          <p className="text-3xl font-semibold text-yellow-600">
            {friendRequestsData.summary.unmatched}
          </p>
          <p className="text-sm text-slate-500">Unmatched</p>
        </div>
      </div>

      {/* Mutual Matches Section */}
      {friendRequestsData.matches.length > 0 && (
        <div className="program-data-panel">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-green-500" />
            <h3 className="font-semibold text-slate-900">Mutual Matches</h3>
            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
              {friendRequestsData.matches.length} {friendRequestsData.matches.length === 1 ? "pair" : "pairs"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            These players have mutually requested each other. Consider placing
            them on the same team.
          </p>
          <div className="space-y-3">
            {friendRequestsData.matches.map((match, idx) => (
              <div
                key={idx}
                className="program-friend-match flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <UserRound className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="font-medium text-slate-900">
                      {match.player1_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-green-600">
                    <Link className="w-4 h-4" />
                    <span className="text-sm">matched with</span>
                    <Link className="w-4 h-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <UserRound className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="font-medium text-slate-900">
                      {match.player2_name}
                    </span>
                  </div>
                </div>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Friend Requests */}
      <div className="program-data-panel">
        <h3 className="font-semibold text-slate-900 mb-4">
          All Friend Requests by Player
        </h3>
        {friendRequestsData.registrations.length > 0 ? (
          <div className="space-y-4">
            {friendRequestsData.registrations.map((reg) => (
              <div
                key={reg.registration_id}
                className={`program-friend-row py-3 border-b ${
                  reg.friend_requests_processed
                    ? "bg-slate-50 border-slate-200"
                    : "bg-white border-slate-200/60"
                }`}
              >
                <div className="flex flex-wrap gap-3 items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                      {reg.player_photo ? (
                        <img
                          loading="lazy"
                          src={reg.player_photo}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <UserRound className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {reg.player_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {reg.friend_requests?.length || 0} friend request
                        {(reg.friend_requests?.length || 0) !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {reg.friend_requests_processed ? (
                      <span className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded-full">
                        <Check className="w-3 h-3" />
                        Processed
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                        onClick={() =>
                          onMarkProcessed(reg.registration_id, true)
                        }
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Mark Processed
                      </Button>
                    )}
                  </div>
                </div>

                {/* Friend Requests List */}
                <div className="mt-4 pl-15">
                  <div className="flex flex-wrap gap-2">
                    {reg.friend_requests?.map((fr, idx) => (
                      <div
                        key={idx}
                        className="conditional-request-chip flex items-center gap-2 px-2 py-1"
                      >
                        <Heart className="w-4 h-4 text-blue-500" />
                        <span className="text-sm text-slate-700">
                          {fr.displayName || fr.value}
                        </span>
                        <span className="text-xs text-slate-400">
                          (
                          {fr.type === "player"
                            ? "selected"
                            : fr.type === "code"
                            ? "code"
                            : "name"}
                          )
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes if processed */}
                {reg.friend_requests_processed && reg.friend_requests_notes && (
                  <div className="mt-3 p-2 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-500">
                      Note: {reg.friend_requests_notes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Heart className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-slate-500">No friend requests submitted yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramFriendRequestsTab;
